import { useState } from 'react';
import { ItemCategory, ItemCommissionType, Items } from '@prisma/client';
import { Box, capitalize } from '@mui/material';
import { Form, Formik } from 'formik';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { FormikForm, InputRowType } from '../../interfaces';
import {
  itemForm,
  ItemForm as ItemFormType,
  ItemWithCommissions,
} from '../../types/items';
import useUser from '../../hooks/global/useUser';
import InputGroup from '../form/InputGroup';
import EditableInfoSection from '../form/EditableInfoSection';
import ItemCommissionsModal from './ItemCommissionsModal';
import { getCommissionEmployees } from '../../helpers/items';
import useCreateItem from '../../hooks/items/useCreateItem';
import useUpdateItem from '../../hooks/items/useUpdateItem';
import { invalidateItems, invalidateSearchItems } from '../../services/items';
import { Optional } from '../../types/global';
import useGetOnboarding from '../../hooks/businesses/useGetOnboarding';

interface Props {
  item?: Optional<ItemWithCommissions, 'id'> | null;
  onSuccess?: (item: Items) => void | Promise<void>;
}

function ItemForm({ item, onSuccess: onSuccessProp }: Props) {
  const { user } = useUser();
  const { reloadAfterUpdate } = useGetOnboarding();

  const [isEditCommissionModalOpen, setIsEditCommissionModalOpen] =
    useState(false);

  const onSuccess = async (successItem: Items) => {
    await invalidateItems();
    await invalidateSearchItems();
    reloadAfterUpdate('ITEM_LIBRARY');
    onSuccessProp?.(successItem);

    toast.success(`Item ${item?.id ? 'updated' : 'created'}!`);
  };

  const onError = (error: string) => {
    toast.error(error);
  };

  const { mutateAsync: createItem, isPending: isPendingCreate } = useCreateItem(
    {
      onSuccess,
      onError,
    },
  );
  const { mutateAsync: updateItem, isPending: isPendingUpdate } = useUpdateItem(
    {
      onSuccess,
      onError,
    },
  );

  const isPending = isPendingCreate || isPendingUpdate;

  const productTaxRate = user?.business?.businessTaxRates?.productTaxRate || 0;

  const form: FormikForm<ItemFormType> = {
    onSubmit: async values => {
      try {
        if (item?.id) {
          await updateItem({
            itemId: item.id,
            payload: values,
          });
        } else {
          await createItem({
            payload: values,
          });
        }
      } catch (error) {
        // Do nothing
      }
    },
    initialValues: {
      name: item?.name || '',
      category: (item?.category || '') as ItemCategory,
      price: (item ? item.price || 0 : '') as number,
      isTaxable: item?.isTaxable ?? productTaxRate > 0,
      commissionType: item?.commissionType || 'REVENUE',
      employeeCommissions: item?.employeeCommissions || [],
      cost: (item ? item.cost || 0 : '') as number,
    },
    validationSchema: itemForm,
  };

  const employeesAvailableForCommissions =
    user?.business?.type === 'STUDIO'
      ? getCommissionEmployees(user?.business?.employees)
      : [];

  return (
    <Box>
      <Formik
        enableReinitialize
        initialValues={form.initialValues}
        validationSchema={form.validationSchema}
        onSubmit={form.onSubmit}>
        {({ isSubmitting, values, isValid, dirty: formikDirty }) => (
          <Form>
            <InputGroup
              name="Item Details"
              rows={[
                [
                  {
                    label: 'Name *',
                    name: 'name',
                    type: 'text',
                    placeholder: 'Enter item name',
                  },
                ],
                [
                  {
                    label: 'Category *',
                    name: 'category',
                    type: 'select',
                    placeholder: 'Select category',
                    options: Object.values(ItemCategory).map(category => ({
                      label: capitalize(category.toLowerCase()),
                      value: category,
                    })),
                    autoWidth: false,
                  },
                ],
                [
                  {
                    label: 'Default Price *',
                    name: 'price',
                    type: 'dollar',
                    placeholder: 'Enter default price',
                  },
                ],
                [
                  {
                    label: 'Sales Tax *',
                    name: 'isTaxable',
                    type: 'select',
                    placeholder: 'Select sales tax',
                    options: [
                      {
                        label: 'Non-taxable item',
                        value: false,
                      },
                      {
                        label: `Collect product sales tax (${
                          productTaxRate * 100
                        }%)`,
                        value: true,
                      },
                    ],
                    autoWidth: false,
                  },
                ],
              ]}
            />

            {!!employeesAvailableForCommissions?.length && (
              <>
                <EditableInfoSection
                  title="Payout Details"
                  description="Optionally, add in commission rates for each user. Whenever this product is sold and attached to a service provider, we’ll split up the payment based on their designated commission rate . Only users that are paid on commissions can be set up with a product commission rate. "
                  rows={employeesAvailableForCommissions.map(employee => ({
                    title:
                      employee.displayName ||
                      employee.user?.firstName ||
                      'Unnamed',
                    value: `${
                      (values.employeeCommissions?.find(
                        ({ employeeId }) => employeeId === employee.employeeId,
                      )?.rate || 0) * 100
                    }%`,
                  }))}
                  button={{
                    text: 'Edit Commissions',
                    onClick: () => setIsEditCommissionModalOpen(true),
                  }}
                />
                {isEditCommissionModalOpen && (
                  <ItemCommissionsModal
                    open
                    onClose={() => setIsEditCommissionModalOpen(false)}
                  />
                )}
              </>
            )}

            <InputGroup
              name="Other Details"
              rows={
                [
                  [
                    {
                      label: 'Cost',
                      name: 'cost',
                      type: 'dollar',
                      placeholder: 'Enter cost',
                      tooltip: 'The cost to stock this item',
                    },
                  ],
                  !!values.employeeCommissions?.some(({ rate }) => rate > 0) &&
                  !!employeesAvailableForCommissions?.length
                    ? [
                        {
                          label: 'Commission Type *',
                          name: 'commissionType',
                          type: 'select',
                          placeholder: 'Select commission type',
                          options: [
                            {
                              label: 'Pay commissions on Revenue',
                              value: ItemCommissionType.REVENUE,
                            },
                            {
                              label: 'Pay commissions on Profit',
                              value: ItemCommissionType.PROFIT,
                            },
                          ],
                          autoWidth: false,
                        },
                      ]
                    : null,
                ].filter(Boolean) as InputRowType[]
              }
            />

            <LoadingButton
              type="submit"
              disabled={!isValid || isSubmitting || !formikDirty || isPending}
              loading={isPending || isSubmitting}
              variant="contained"
              className="primary">
              {item?.id ? 'Save' : 'Create'}
            </LoadingButton>
          </Form>
        )}
      </Formik>
    </Box>
  );
}

export default ItemForm;
